import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Badge, Card, Text, Title } from '@tremor/react';
import { fetchInvRecord } from '../api/invRecord';
import type { InvRecord } from '../api/types';
import { t } from '../lib/i18n';
import DrillCell from './DrillCell';
import EmptyState from './EmptyState';
import ErrorRetry from './ErrorRetry';
import IconButton from './IconButton';
import Modal from './Modal';
import TableSkeleton from './TableSkeleton';
import { BackIcon, CloseIcon } from './icons';

interface Props {
  sid: number;
  line: string;
  part: string;
  onClose: () => void;
  /** Supplied by DrilldownHost when this modal sits on top of another. */
  onBack?: () => void;
}

const fmtMxn = new Intl.NumberFormat('es-MX', {
  style: 'currency', currency: 'MXN', minimumFractionDigits: 2,
});

const money = (v: number | null): string | null => (v === null ? null : fmtMxn.format(v));

/** One label/value row. Renders nothing when the value is absent. */
function Field({ label, value }: { label: string; value: string | number | null | undefined }) {
  if (value === null || value === undefined || value === '') return null;
  return (
    <div className="flex justify-between gap-4 py-1 border-b border-tremor-border last:border-0">
      <Text className="text-tremor-content">{label}</Text>
      <Text className="font-medium tabular-nums text-tremor-content-strong">{value}</Text>
    </div>
  );
}

/** A section card; renders nothing when it would be empty. */
function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Card className="p-4">
      <Title className="text-base mb-2">{title}</Title>
      {children}
    </Card>
  );
}

/**
 * The inv record for ONE store, fetched by key (JCD-59).
 *
 * This replaces the old `/parts?q=<part>` link, which ran a substring search on
 * the parts page and threw away the store the row belonged to — so a click on a
 * store-3 row could answer with a different store's part.
 */
export default function InvRecordModal({ sid, line, part, onClose, onBack }: Props) {
  // undefined = still loading, null = no record at this store.
  const [rec, setRec] = useState<InvRecord | null | undefined>(undefined);
  const [err, setErr] = useState<string | null>(null);
  // Bumping this re-runs the effect below, so the retry path and the initial
  // load stay one code path.
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    let alive = true;
    setErr(null);
    setRec(undefined);
    fetchInvRecord(sid, line, part)
      .then((r) => { if (alive) setRec(r); })
      .catch((e: Error) => { if (alive) setErr(e.message); });
    return () => { alive = false; };
  }, [sid, line, part, retry]);

  // A cross-referenced part is the same store's shelf, so following a
  // substitute stays in this store instead of bouncing to a catalog view.
  const xref = (l: string, p: string) =>
    l && p ? { kind: 'inv' as const, sid, line: l, part: p } : null;

  const hasStoreCost = rec ? rec.cost_mxn !== null || rec.cost_usd !== null : false;
  const hasFactoryCost = rec
    ? rec.factory_cost_mxn !== null || rec.factory_cost_usd !== null || rec.factory_core_cost !== null
    : false;
  const hasFactory = rec
    ? rec.factory_list_price !== null || rec.factory_price1 !== null || hasFactoryCost
    : false;
  const hasXrefs = rec
    ? !!(rec.sub_part || rec.sub_from_part || rec.alt1_part || rec.alt2_part
      || rec.part_comp || rec.codes)
    : false;

  return (
    <Modal onClose={onClose} ariaLabel={t.drilldown.invTitle} size="4xl" panelClassName="p-5">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-start gap-2 min-w-0">
          {onBack && (
            <IconButton onClick={onBack} label={t.drilldown.back} size="sm">
              <BackIcon />
            </IconButton>
          )}
          <div className="min-w-0">
            <Title className="truncate">{line} · {part}</Title>
            {rec && (
              <Text className="truncate text-tremor-content-strong">{rec.pdesc}</Text>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {rec && <Badge color="blue">{rec.store_name}</Badge>}
          <IconButton onClick={onClose} label={t.drilldown.close}>
            <CloseIcon />
          </IconButton>
        </div>
      </div>

      {rec === undefined && !err && <TableSkeleton rows={6} cols={2} />}

      {err && <ErrorRetry message={err} onRetry={() => setRetry((n) => n + 1)} />}

      {!err && rec === null && <EmptyState message={t.drilldown.notStocked} />}

      {!err && rec && (
        <div className="space-y-4">
          <Section title={t.drilldown.thisStore}>
            <Field label={t.drilldown.onHand} value={rec.on_hand} />
            <Field label={t.drilldown.min} value={rec.min_stock} />
            <Field label={t.drilldown.max} value={rec.max_stock} />
            <Field label={t.drilldown.bin} value={rec.bin} />
            <Field label={t.drilldown.phydate} value={rec.phydate ?? t.drilldown.never} />
            <Field label={t.drilldown.edate} value={rec.edate} />
          </Section>

          <Section title={t.drilldown.prices}>
            <Field label={t.drilldown.price1} value={money(rec.price1_mxn)} />
            <Field label={t.drilldown.price2} value={money(rec.price2_mxn)} />
            <Field label={t.drilldown.price3} value={money(rec.price3_mxn)} />
            <Field label={t.drilldown.listPrice} value={money(rec.store_list_price)} />
          </Section>

          {hasStoreCost && (
            <Section title={t.drilldown.costs}>
              <Field label={t.drilldown.costMxn} value={money(rec.cost_mxn)} />
              <Field label={t.drilldown.costUsd} value={money(rec.cost_usd)} />
            </Section>
          )}

          {hasFactory && (
            <Section title={t.drilldown.factory}>
              <Field label={t.drilldown.factoryList} value={money(rec.factory_list_price)} />
              <Field label={t.drilldown.factoryPrice1} value={money(rec.factory_price1)} />
              <Field label={t.drilldown.factoryPrice2} value={money(rec.factory_price2)} />
              <Field label={t.drilldown.factoryPrice3} value={money(rec.factory_price3)} />
              {hasFactoryCost && (
                <>
                  <Field label={t.drilldown.factoryCostMxn} value={money(rec.factory_cost_mxn)} />
                  <Field label={t.drilldown.factoryCostUsd} value={money(rec.factory_cost_usd)} />
                  <Field label={t.drilldown.factoryCoreCost} value={money(rec.factory_core_cost)} />
                </>
              )}
            </Section>
          )}

          {hasXrefs && (
            <Section title={t.drilldown.crossRefs}>
              {rec.sub_part && (
                <div className="flex justify-between gap-4 py-1 border-b border-tremor-border">
                  <Text className="text-tremor-content">{t.drilldown.substitute}</Text>
                  <DrillCell target={xref(rec.sub_line, rec.sub_part)}>
                    {rec.sub_line} {rec.sub_part}
                  </DrillCell>
                </div>
              )}
              {rec.sub_from_part && (
                <div className="flex justify-between gap-4 py-1 border-b border-tremor-border">
                  <Text className="text-tremor-content">{t.drilldown.supersededFrom}</Text>
                  <DrillCell target={xref(rec.sub_from_line, rec.sub_from_part)}>
                    {rec.sub_from_line} {rec.sub_from_part}
                  </DrillCell>
                </div>
              )}
              {rec.alt1_part && (
                <div className="flex justify-between gap-4 py-1 border-b border-tremor-border">
                  <Text className="text-tremor-content">{t.drilldown.alt1}</Text>
                  <DrillCell target={xref(rec.alt1_line, rec.alt1_part)}>
                    {rec.alt1_line} {rec.alt1_part}
                  </DrillCell>
                </div>
              )}
              {rec.alt2_part && (
                <div className="flex justify-between gap-4 py-1 border-b border-tremor-border">
                  <Text className="text-tremor-content">{t.drilldown.alt2}</Text>
                  <DrillCell target={xref(rec.alt2_line, rec.alt2_part)}>
                    {rec.alt2_line} {rec.alt2_part}
                  </DrillCell>
                </div>
              )}
              <Field label={t.drilldown.partComp} value={rec.part_comp} />
              <Field label={t.drilldown.codes} value={rec.codes} />
            </Section>
          )}

          <div className="pt-1">
            <Link
              to={`/parts/${encodeURIComponent(line)}/${encodeURIComponent(part)}`}
              onClick={onClose}
              className="text-tremor-brand hover:underline"
            >
              {t.drilldown.fullPart}
            </Link>
          </div>
        </div>
      )}
    </Modal>
  );
}
